"use client"

import { useEffect, useRef, useState } from "react"

export type ScrollDirection = "up" | "down"
export type HeaderVisibilityState = { passed: boolean; visible: boolean }
export type HeaderVisibilityEvent = { passed: boolean; direction: ScrollDirection }

export function reduceHeaderVisibility(
  _state: HeaderVisibilityState,
  event: HeaderVisibilityEvent
): HeaderVisibilityState {
  if (!event.passed) return { passed: false, visible: false }
  return { passed: true, visible: event.direction === "up" }
}

export function useScrollHeader() {
  const [state, setState] = useState<HeaderVisibilityState>({ passed: false, visible: false })
  const sentinelRef = useRef<HTMLDivElement>(null)
  const passedRef = useRef(false)
  const directionRef = useRef<ScrollDirection>("down")
  const lastScrollYRef = useRef(0)
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    const publish = () => {
      if (frameRef.current !== null) return
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null
        setState((current) =>
          reduceHeaderVisibility(current, {
            passed: passedRef.current,
            direction: directionRef.current,
          })
        )
      })
    }

    lastScrollYRef.current = window.scrollY
    const onScroll = () => {
      const next = window.scrollY
      if (next > lastScrollYRef.current) directionRef.current = "down"
      if (next < lastScrollYRef.current) directionRef.current = "up"
      lastScrollYRef.current = next
      publish()
    }
    window.addEventListener("scroll", onScroll, { passive: true })

    const sentinel = sentinelRef.current
    const observer = sentinel && "IntersectionObserver" in window
      ? new IntersectionObserver(([entry]) => {
          if (!entry) return
          passedRef.current = !entry.isIntersecting
          publish()
        }, { threshold: 0 })
      : null
    if (sentinel && observer) observer.observe(sentinel)

    return () => {
      window.removeEventListener("scroll", onScroll)
      observer?.disconnect()
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current)
    }
  }, [])

  return { ...state, sentinelRef }
}
